import { connect } from 'react-redux';
import lodash from 'lodash';
import { Fragment, useState } from 'react';

import styles from './ConfirmRemovalPanel.scss';

import { Box, Typography, Checkbox, FormControlLabel } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { Button, BUTTON } from 'components/common/Button/Button';
import { Conditional } from 'common/components/layout';

import { removeStudyTasksV2, saveStudyTaskV2 } from 'pages/delegationOfAuthority/v2/actions/studyTasksV2';
import { delegationsList, getActivePI } from 'pages/delegationOfAuthority/selectors';

import {
    delegationSignedPredicate,
    pendingPrincipalSignaturePredicate,
    completePrincipalSignaturePredicate,
    pendingStaffSignaturePredicate,
    completeStaffSignaturePredicate,
    hasTaskToRemovePredicate,
    piHasPendingSignature,
    matchPiToDoaList,
} from 'pages/delegationOfAuthority/v2/util/predicates';

const STAFF_START = 'STAFF_START';
const PI_START = 'PI_START';
const PI_LABEL = 'P.I';
const STAFF_LABEL = 'Staff';

function ConfirmRemovalPanel(props) {
    const {
        binderId,
        delegationsEffected,
        delegationsSigned,
        delegationsUnsigned,
        onClose,
        principalInvestigator,
        removeStudyTasksV2,
        saveStudyTaskV2,
        studyId,
        tasksToKeep,
        tasksToRemove,
        tasksWithSignedDelegations,
        tasksWithUnsignedDelegations,
        tasksWithoutDelegations,
    } = props;

    const [auditNote, setAuditNote] = useState(undefined);
    const [signaturesToSend, setSignaturesToSend] = useState([]);

    const handleConfirmChanges = () => {
        const delegations = [];
        const defaultSignatureExpectations = [];
        const signatureExpectations = [];
        const principalSignatureExpectations = [];

        for (const delegation of delegationsEffected) {
            const newSignatureRequested = signaturesToSend.find((s) => s.id === delegation.id);

            delegations.push({
                id: delegation.id,
                user: {
                    id: delegation.user.id,
                    name: delegation.user.displayName,
                },
                binderId,
                studyId,
                roleManagementId: delegation.role.id,
                tasks: delegation.tasks.map((task) => {
                    return {
                        id: task.taskId,
                        taskName: task.taskName,
                        taskIndex: task.taskIndex
                    };
                }),
                startDate: delegation.startDate,
                endDate: delegation.endDate,
                notes: delegation.signed ? auditNote : undefined,
            });

            if (!delegation.isPrincipalInvestigator && newSignatureRequested) {
                signatureExpectations.push({
                    delegationId: delegation.id,
                    user: {
                        id: delegation.user.id,
                        name: delegation.user.displayName
                    },
                    signatureType: STAFF_START,
                });
            }

            if (!delegation.isPrincipalInvestigator) {
                defaultSignatureExpectations.push({
                    delegationId: delegation.id,
                    user: {
                        id: delegation.user.id,
                        name: delegation.user.displayName
                    },
                    signatureType: STAFF_START,
                });
            }

            if (principalInvestigator) {
                principalSignatureExpectations.push({
                    delegationId: delegation.id,
                    user: {
                        id: principalInvestigator.user.id,
                        name: principalInvestigator.user.displayName
                    },
                    signatureType: PI_START,
                });
            }
        }

        defaultSignatureExpectations.push(...principalSignatureExpectations);

        const principalSignatureRequested =
            principalInvestigator ? signaturesToSend.find((s) => s.id === principalInvestigator.id) : false;

        if (principalSignatureRequested) {
            signatureExpectations.push(...principalSignatureExpectations);
        }

        saveStudyTaskV2(tasksToKeep, binderId, studyId);

        removeStudyTasksV2(
            binderId, studyId, tasksToRemove, delegations, signatureExpectations, defaultSignatureExpectations, onClose
        );
    };

    const handleCheckboxClick = (e, user) => {
        if (e.target.checked) {
            setSignaturesToSend((prevState) => [...prevState, user]);
        }

        if (!e.target.checked) {
            setSignaturesToSend((prevState) => {
                const newState = prevState.filter((s) => s.id !== user.id);
                return newState;
            });
        }
    };

    const disableConfirmChanges = !lodash.isEmpty(delegationsSigned) && lodash.isEmpty(auditNote);

    const getDelegationRoleLabel = (delegation) => {
        let label = STAFF_LABEL;

        if (delegation.isPrincipalInvestigator) {
            label = PI_LABEL;
        }

        return (
            <Fragment>
                <span style={{ fontWeight: 'bold' }}>{delegation.user.displayName}</span>
                <span style={{ marginLeft: '0.5rem', fontWeight: 'normal' }}>( {label} )</span>
            </Fragment>
        );
    };

    const renderRemovalTasks = (tasks, delegations, signedDelegation = false) => {
        return (
            <Fragment>
                <Typography variant="body2" gutterBottom={true}>Tasks</Typography>

                {!lodash.isEmpty(tasks) && tasks.map((task) => {
                    return (
                        <Typography variant="body1" gutterBottom={true} key={task.id} style={{ fontWeight: '500' }}>
                            {task.taskName}
                        </Typography>
                    );
                })}

                <Conditional condition={!lodash.isEmpty(delegations)}>
                    <Box my={2}>
                        <Typography variant="h6">Re-Request Signatures</Typography>
                        {delegations && delegations.map((delegation) => {
                            return (
                                <div key={delegation.id}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox disableRipple color="primary" size="small"
                                                onChange={(e) => handleCheckboxClick(e, { ...delegation, signedDelegation })} />
                                        }
                                        label={getDelegationRoleLabel(delegation)}
                                    />
                                </div>
                            );
                        })}
                    </Box>
                </Conditional>

                <Conditional condition={signedDelegation}>
                    <Box mb={3}>
                        <div className={styles.AuditNote}>
                            <h5 className={styles.AuditNote__title}>
                                Audit Notes  <span className={styles.AuditNote__required}>*Required</span>
                            </h5>

                            <textarea
                                className={styles.AuditNote__area}
                                placeholder={'Add notes to the audit record of this delegation...'}
                                value={auditNote}
                                rows={4}
                                onChange={(event) => setAuditNote(event.target.value)}
                            ></textarea>
                        </div>
                    </Box>
                </Conditional>
            </Fragment>
        );
    };

    return (
        <Box className={styles.Container}>
            <Box className={styles.Container__Removal__Header}>
                <Box className={styles.Header}>
                    <Typography variant="h6" color="primary">Confirm Removal of Study Tasks</Typography>
                    <CloseIcon className={styles.Header__CloseIcon} onClick={onClose} />
                </Box>
            </Box>

            <Box className={styles.Container__Removal__List}>
                <Conditional condition={!lodash.isEmpty(tasksWithoutDelegations)}>
                    <div className={styles.tasks__section__first} id="unassigned__Tasks">
                        <Typography variant="h6" gutterBottom={true}>Tasks that aren’t assigned to Delegations</Typography>

                        <Box className={styles.BlockInfo}>
                            <Typography variant="body2">
                                The following tasks weren’t assigned to delegations and will be removed from the Study Task List. No further action is required.
                            </Typography>
                        </Box>

                        {renderRemovalTasks(tasksWithoutDelegations)}
                    </div>
                </Conditional>

                <Conditional condition={!lodash.isEmpty(delegationsUnsigned)}>
                    <div className={styles.tasks__section} id="unsigned__delegation__assigned__tasks">
                        <Typography variant="h6" gutterBottom={true}>Tasks assigned to Delegations that haven’t been signed</Typography>

                        <Box className={styles.BlockInfo}>
                            <Typography variant="body2">
                                Anytime a task list modification affects unsigned delegations, you have the option to re-request signatures from relevant staff/ PIs.
                            </Typography>
                        </Box>

                        {renderRemovalTasks(tasksWithUnsignedDelegations, delegationsUnsigned)}
                    </div>
                </Conditional>

                <Conditional condition={!lodash.isEmpty(delegationsSigned)}>
                    <div style={{ paddingTop: '20px' }} id="signed__delegations__assigned__tasks">
                        <Typography variant="h6" gutterBottom={true}>Tasks assigned to Delegations that have been signed</Typography>

                        <Box className={styles.BlockInfo}>
                            <Typography variant="body2">
                                Anytime a task list modification affects signed delegations, you have the option to re-request signatures from relevant staff/ PIs. You are also required to provide an explanation for those delegations.
                            </Typography>
                        </Box>

                        {renderRemovalTasks(tasksWithSignedDelegations, delegationsSigned, true)}
                    </div>
                </Conditional>
            </Box>

            <Box className={styles.Container__Footer}>
                <Button text={'Cancel'}
                    outline={BUTTON.OUTCOLOR.MEDIUMGREY}
                    color={BUTTON.COLOR.BLACK}
                    bgcolor={BUTTON.BGCOLOR.WHITE}
                    onClick={onClose}
                    data-testid="Removal__Cancel__Btn"
                />
                <Button text={'Confirm Changes'}
                    bgcolor={BUTTON.BGCOLOR.ORANGE}
                    color={BUTTON.COLOR.WHITE}
                    disabled={disableConfirmChanges}
                    onClick={handleConfirmChanges}
                    data-testid="Removal__Confirm__Changes__Btn"
                />
            </Box>
        </Box>
    );
}

export default connect(
    (state, props) => {
        const binderId = lodash.get(state, 'binder.id');
        const studyId = lodash.get(state, 'study.id');
        const centralized = lodash.get(state, 'me.isInstitutionCentralized') === 'true' ? true : false;

        const tasksToKeep = lodash.get(props, 'currentTaskList', []);
        const tasksToRemove = lodash.get(props, 'removedTaskList', []);

        const principalInvestigator = getActivePI(state);
        const delegations = delegationsList(state) || [];

        const [
            signedDelegationsLosingTasks,
            unsignedDelegationsLosingTasks,
        ] = filterDelegations(centralized, delegations, principalInvestigator, tasksToRemove);

        const [
            delegationsSigned,
            delegationsUnsigned,
        ] = filterDelegationTasks(tasksToRemove, signedDelegationsLosingTasks, unsignedDelegationsLosingTasks);

        const [
            tasksWithDelegations,
            tasksWithoutDelegations,
        ] = filterTasks(delegations, tasksToRemove);

        const [
            tasksWithSignedDelegations,
            tasksWithUnsignedDelegations,
        ] = filterTasksOnSignature(tasksWithDelegations, signedDelegationsLosingTasks, unsignedDelegationsLosingTasks);

        return {
            binderId,
            delegationsEffected: [...delegationsUnsigned, ...delegationsSigned],
            delegationsSigned,
            delegationsUnsigned,
            principalInvestigator,
            studyId,
            tasksToKeep,
            tasksToRemove,
            tasksWithSignedDelegations,
            tasksWithUnsignedDelegations,
            tasksWithoutDelegations
        };
    },
    {
        removeStudyTasksV2,
        saveStudyTaskV2
    }
)(ConfirmRemovalPanel);

const filterDelegations = (centralized, delegations, principalInvestigator, tasksToRemove) => {
    const signedDelegationsLosingTasks = [];
    const unsignedDelegationsLosingTasks = [];

    for (const delegation of delegations) {
        const isPrincipalInvestigator = principalInvestigator ? delegation.id === principalInvestigator.id : false;

        const options = {
            hasPendingPrincipalSignature: pendingPrincipalSignaturePredicate(delegation),
            hasCompletePrincipalSignature: completePrincipalSignaturePredicate(delegation),
            hasPendingStaffSignature: pendingStaffSignaturePredicate(delegation),
            hasCompleteStaffSignature: completeStaffSignaturePredicate(delegation)
        };

        const signed = delegationSignedPredicate(centralized, delegation, isPrincipalInvestigator, options);

        const delegationHasTasksToRemove = delegation.taskIds.some(hasTaskToRemovePredicate(tasksToRemove));

        if (delegationHasTasksToRemove && !isPrincipalInvestigator) {
            if (signed) {
                signedDelegationsLosingTasks.push(
                    { ...delegation, ...options, isPrincipalInvestigator, signed }
                );
            } else {
                unsignedDelegationsLosingTasks.push(
                    { ...delegation, ...options, isPrincipalInvestigator, signed }
                );
            }

        }
    }

    if (principalInvestigator) {
        // Check if PI doa has tasks being effect.
        const piDelegation = delegations.find(doa => doa.id === principalInvestigator.id);
        const piTaskIds = lodash.get(piDelegation, 'taskIds', []);
        const isPiLosingTasks = piTaskIds.some(hasTaskToRemovePredicate(tasksToRemove));
        const isDelegationsLosingTasks = signedDelegationsLosingTasks.length > 0 || unsignedDelegationsLosingTasks.length > 0;

        if (isDelegationsLosingTasks) {
            // Mutates the input arrays.
            correctlyPlacePiInDoaLosingTasks({
                piDelegation,
                principalInvestigator,
                signedDelegationsLosingTasks,
                unsignedDelegationsLosingTasks,
            });
        }

        if (isPiLosingTasks) {
            correctlyPlacePiInDoaLosingTasks({
                piDelegation,
                principalInvestigator,
                signedDelegationsLosingTasks,
                unsignedDelegationsLosingTasks,
                processPi: true
            });
        }
    }

    return [
        signedDelegationsLosingTasks,
        unsignedDelegationsLosingTasks,
    ];
};

const correctlyPlacePiInDoaLosingTasks = ({
    piDelegation,
    principalInvestigator,
    signedDelegationsLosingTasks,
    unsignedDelegationsLosingTasks,
    processPi = false,
}) => {

    let doas = [...signedDelegationsLosingTasks, ...unsignedDelegationsLosingTasks];

    if (processPi === true) {
        doas = [piDelegation];
    }

    // Aggregate all signatures into groups, easier for processing
    const allSignaturesGroups = doas
        .reduce((accumulator, current) => {
            if (current.signatures) {
                return [...accumulator, current.signatures];
            }

            return accumulator;
        }, []);

    // for each group of signatures, find out if PI need to be added to signed/unsigned arrays.
    for (const signatureGroup of allSignaturesGroups) {
        const signedHasPi = signedDelegationsLosingTasks.find(matchPiToDoaList(principalInvestigator));
        const unsignedHasPi = unsignedDelegationsLosingTasks.find(matchPiToDoaList(principalInvestigator));

        if (!piHasPendingSignature(signatureGroup) && !unsignedHasPi) {
            unsignedDelegationsLosingTasks.push({ ...piDelegation, isPrincipalInvestigator: true });
        }

        if (piHasPendingSignature(signatureGroup) && !signedHasPi) {
            signedDelegationsLosingTasks.push({ ...piDelegation, isPrincipalInvestigator: true, signed: true });
        }
    }
};

const filterDelegationTasks = (tasksToRemove, signedDelegationsLosingTasks, unsignedDelegationsLosingTasks) => {
    const delegationsSigned = [];
    const delegationsUnsigned = [];

    const tasksToRemoveKeys = tasksToRemove.map((task) => task.id);

    for (const delegation of signedDelegationsLosingTasks) {
        const remainingTasksForDelegation = delegation.tasks.filter((task) => !tasksToRemoveKeys.includes(task.taskId));
        delegationsSigned.push({ ...delegation, tasks: remainingTasksForDelegation });
    }

    for (const delegation of unsignedDelegationsLosingTasks) {
        const remainingTasksForDelegation = delegation.tasks.filter((task) => !tasksToRemoveKeys.includes(task.taskId));
        delegationsUnsigned.push({ ...delegation, tasks: remainingTasksForDelegation });
    }

    return [
        delegationsSigned,
        delegationsUnsigned,
    ];
};

const filterTasks = (delegations, tasksToRemove) => {
    const tasksWithDelegations = [];
    const tasksWithoutDelegations = [];

    for (const task of tasksToRemove) {
        let assigned = false;

        for (const delegation of delegations) {
            assigned = delegation.tasks.some((t) => t.taskId == task.id);

            if (assigned) {
                break;
            }
        }

        if (assigned) {
            tasksWithDelegations.push(task);
        } else {
            tasksWithoutDelegations.push(task);
        }
    }

    return [
        tasksWithDelegations,
        tasksWithoutDelegations,
    ];
};

const filterTasksOnSignature = (tasksWithDelegations, signedDelegationsLosingTasks, unsignedDelegationsLosingTasks) => {
    // Using maps here to dedup tasks we're adding for PIs
    let tasksWithSignedDelegations = new Map();
    let tasksWithUnsignedDelegations = new Map();

    for (const task of tasksWithDelegations) {
        const taskToRemoveOnSignedDelegation = signedDelegationsLosingTasks.flatMap((d) => d.taskIds).includes(task.id);

        if (taskToRemoveOnSignedDelegation) {
            tasksWithSignedDelegations.set(task.id, task);
        }

        const taskToRemoveOnUnsignedDelegation = unsignedDelegationsLosingTasks.flatMap((d) => d.taskIds).includes(task.id);

        if (taskToRemoveOnUnsignedDelegation) {
            tasksWithUnsignedDelegations.set(task.id, task);
        }
    }

    /**
     * Look through all signed DOAs,
     * get all staff DOAs,
     * check if it has a pending signature from PI (basically the opposite of the DOA current signature state)
     * check against the list of tasks that are being removed
     * 
     * This also assumes that the PI doa is in the right place, which it should be
     * via the filtering from filterDelegations
     */
    signedDelegationsLosingTasks
        .filter(doa => doa.hasPendingPrincipalSignature === true)
        .forEach(doa => {
            Array.from(tasksWithSignedDelegations.values())
                .filter((task) => doa.taskIds.includes(task.id))
                .forEach((task) => tasksWithUnsignedDelegations.set(task.id, task));
        });


    /**
     * Look through all unsigned DOAs,
     * get all staff DOAs,
     * check if it has a completed signature from PI (basically the opposite of the DOA current signature state)
     * check against the list of tasks that are being removed
     * 
     * This also assumes that the PI doa is in the right place, which it should be
     * via the filtering from filterDelegations
     */
    unsignedDelegationsLosingTasks
        .filter(doa => doa.hasCompletePrincipalSignature === true)
        .forEach(doa => {
            Array.from(tasksWithUnsignedDelegations.values())
                .filter((task) => doa.taskIds.includes(task.id))
                .forEach((task) => tasksWithSignedDelegations.set(task.id, task));
        });

    // Maps aren't arrays, so got to convert them here.
    tasksWithSignedDelegations = Array.from(tasksWithSignedDelegations.values());
    tasksWithUnsignedDelegations = Array.from(tasksWithUnsignedDelegations.values());

    return [
        tasksWithSignedDelegations,
        tasksWithUnsignedDelegations,
    ];
};

